/* eslint-disable react/react-in-jsx-scope */
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../AppContext";

import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { SxProps } from "@mui/system/styleFunctionSx/styleFunctionSx";

import MenuIcon from "@mui/icons-material/Menu";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SchemaIcon from "@mui/icons-material/Schema";

import { PowerBiReport, PowerBiDataset } from "../models/models";
import DashboardWebApi from "../services/DashboardWebApi";

import DataLoading from "./DataLoading";

const LeftNav = () => {
  const isAuthenticated = DashboardWebApi.IsAuthenticated();
  const navigate = useNavigate();
  const { embeddingData } = useContext(AppContext);

  const expandedLeftNavWidth = 240;
  const collapsedLeftNavWidth = 48;
  const [leftNavWidth, setLeftNavWidth] = useState(collapsedLeftNavWidth);
  const [leftNavExpanded, setLeftNavExpanded] = useState(false);

  const leftNavTopBoxProps: SxProps = {
    width: 1,
    color: "#24125F",
    backgroundColor: "#FFFFFF",
    mt: "44px",
  };

  const leftNavOuterBoxProps: SxProps = { width: 1, display: "flex" };

  const leftNavInnerBoxLeftProps: SxProps = {
    width: "48px",
    minWidth: "48px",
    textAlign: "center",
    p: 0,
    pt: "4px",
    color: "#24125F",
  };

  const avatarProps: SxProps = {
    width: "22px",
    height: "22px",
    m: 0,
    mt: "2px",
  };

  const leftNavInnerBoxTenantNameProps: SxProps = {
    pt: "2px",
    pl: "4px",
    width: leftNavExpanded ? 1 : 0,
    maxHeight: "28px;",
  };

  const leftNavTenantNameProps: SxProps = {
    fontSize: "18px",
    width: 1,
    color: "#24125F",
    pt: "4px",
  };

  const leftNavInnerBoxRightProps: SxProps = {
    py: 0,
    pl: "4px",
    width: leftNavExpanded ? 1 : 0,
    color: "#24125F",
  };

  const leftNavHeaderProps: SxProps = {
    fontSize: "18px",
    width: 1,
    color: "#24125F",
    pl: 0,
    mb: 0,
    my: 0,
    pt: "2px",
  };

  const leftNavListProps: SxProps = {
    m: 0,
    p: 0,
    pb: "8px",
    width: 1,
  };

  const toggleLeftNavWidth = () => {
    if (leftNavExpanded) {
      setLeftNavExpanded(false);
      setLeftNavWidth(collapsedLeftNavWidth);
    } else {
      setLeftNavExpanded(true);
      setLeftNavWidth(expandedLeftNavWidth);
    }
  };

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      data-testid="left-nav-drawer"
      data-expanded={leftNavExpanded}
      data-loading={embeddingData.workspaceArtifactsLoading}
      sx={{
        width: leftNavWidth,
        zIndex: 1,
        pt: "84px",
        pb: 3,
        display:
          isAuthenticated && embeddingData.tenantName
            ? "flex"
            : "none",
      }}
      slotProps={{
        paper: {
          sx: {
            width: leftNavWidth,
            backgroundColor: "#FFFFFF",
            borderRight: "1px solid #24125F",
          },
        },
      }}
    >
      <Box sx={leftNavTopBoxProps}>
        <Box sx={leftNavOuterBoxProps}>
          <Box sx={leftNavInnerBoxLeftProps}>
            <MenuIcon
              sx={avatarProps}
              onClick={toggleLeftNavWidth}
              data-testid="left-nav-menu-icon"
            />
          </Box>
          <Box sx={leftNavInnerBoxTenantNameProps}>
            <Typography
              sx={leftNavTenantNameProps}
              variant="h5"
              fontSize={20}
              data-testid="left-nav-tenant-name"
            >
              {embeddingData.tenantName}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ backgroundColor: "#24125F" }} />

        <Box sx={leftNavOuterBoxProps}>
          <Box sx={leftNavInnerBoxLeftProps}>
            <AssessmentIcon sx={avatarProps} data-testid="left-nav-reports-icon" />
          </Box>
          {leftNavExpanded && (
            <Box sx={leftNavInnerBoxRightProps}>
              <Typography
                sx={leftNavHeaderProps}
                variant="subtitle1"
                data-testid="left-nav-reports-header"
              >
                Reports
              </Typography>
              {embeddingData.workspaceArtifactsLoading && (
                <DataLoading data-testid="left-nav-reports-loading" />
              )}
              {!embeddingData.workspaceArtifactsLoading && (
                <List
                  disablePadding
                  dense
                  sx={leftNavListProps}
                  data-testid="left-nav-reports-list"
                >
                  {embeddingData.reports?.map((report: PowerBiReport) => (
                    <ListItem key={report.id}>
                      <ListItemButton
                        data-testid={`left-nav-report-${report.id}`}
                        data-selected={document.URL.includes(report.id)}
                        sx={{
                          py: "4px",
                          pl: "6px",
                          width: 1,
                          fontWeight: "bold",
                          fontSize: "14px",
                          color: "#24125F",
                          backgroundColor: document.URL.includes(report.id)
                            ? "#DDDDDD"
                            : "#FFFFFF",
                          borderLeft: document.URL.includes(report.id)
                            ? "4px solid #24125F"
                            : "",
                        }}
                        onClick={() => {
                          localStorage.setItem(
                            "defaultreport",
                            `/reports/${report.id}`,
                          );
                          navigate(`/reports/${report.id}`);
                        }}
                      >
                        {report.name}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ backgroundColor: "#24125F" }} />

        {embeddingData.userCanCreate && (
          <>
            <Box sx={leftNavOuterBoxProps}>
              <Box sx={leftNavInnerBoxLeftProps}>
                <SchemaIcon sx={avatarProps} data-testid="left-nav-datasets-icon" />
              </Box>
              {leftNavExpanded && (
                <Box sx={leftNavInnerBoxRightProps}>
                  <Typography
                    sx={leftNavHeaderProps}
                    variant="subtitle1"
                    data-testid="left-nav-datasets-header"
                  >
                    Datasets
                  </Typography>
                  {embeddingData.workspaceArtifactsLoading && (
                    <DataLoading data-testid="left-nav-datasets-loading" />
                  )}
                  {!embeddingData.workspaceArtifactsLoading && (
                    <List
                      disablePadding
                      dense
                      sx={leftNavListProps}
                      data-testid="left-nav-datasets-list"
                    >
                      {embeddingData.datasets?.map(
                        (dataset: PowerBiDataset) => (
                          <ListItem key={dataset.id}>
                            <ListItemButton
                              data-testid={`left-nav-dataset-${dataset.id}`}
                              data-selected={document.URL.includes(dataset.id)}
                              sx={{
                                py: "4px",
                                pl: "6px",
                                width: 1,
                                fontWeight: "bold",
                                fontSize: "14px",
                                color: "#24125F",
                                backgroundColor: document.URL.includes(
                                  dataset.id,
                                )
                                  ? "#DDDDDD"
                                  : "#FFFFFF",
                                borderLeft: document.URL.includes(dataset.id)
                                  ? "4px solid #24125F"
                                  : "",
                              }}
                              onClick={() => {
                                navigate(`/reports/${dataset.id}`);
                              }}
                            >
                              {dataset.name}
                            </ListItemButton>
                          </ListItem>
                        ),
                      )}
                    </List>
                  )}
                </Box>
              )}
            </Box>
            <Divider sx={{ backgroundColor: "#24125F" }} />
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default LeftNav;
